import { matchesModelFilters } from '@context/ProjectFilters/matchesModelFilters';
import useChangeFilterHandler from '@hooks/useChangeFilterHandler';
import {
  ModelFilterCategory,
  useModelFilterActions,
  useModelFilters,
  withCategorySelection,
} from '@store/zustand/model-filters';

/**
 * The store's `setCategory` behind the drawer guard every model-filter setter
 * carries (`useChangeFilterHandler`): the popover's one write path.
 *
 * The selection the write would produce is built first, and the open drawer's
 * entity, or the selected edge's two endpoints, is tested against it with the
 * shared predicate, so the drawer closes before the canvas hides what it
 * shows. An endpoint the canvas no longer holds counts as visible. Clearing a
 * list can only reveal, so it never prompts. When the user declines the
 * unsaved-changes prompt the write is dropped and the tick stays as it was;
 * that decline is the only rejection on this path, so the returned promise
 * always resolves.
 */
export const useSetCategory = () => {
  const changeFilterHandler = useChangeFilterHandler();
  const state = useModelFilters();
  const { setCategory } = useModelFilterActions();

  return (
    category: ModelFilterCategory,
    values: string[],
    criteriaId?: string,
  ) => {
    const next = withCategorySelection(state, category, values, criteriaId);

    return changeFilterHandler({
      isNodeVisible: (_values, node) =>
        !node || matchesModelFilters(node, next),
      onSetValues: (written) => setCategory(category, written, criteriaId),
    })(values).catch(() => undefined);
  };
};
